import React, { useEffect, useRef, useState } from "react";
import { Search, X, ChevronDown, Check } from "lucide-react";
import { QF, quickSearch } from "./erShared";

// Pass A §1 — one field; the chip shows (and can force) which API field the text is sent as.
// Opt-in `collapsible` mode (Claims Queue): the same element shrinks to a 38px icon and
// expands in place on the toolbar row — never a popover. Encounter review uses the defaults.
export function ERSearch({ text, setText, forced, setForced, width, collapsible = false, expanded = true, onExpandedChange, isPhone = false, inGroup = false, placeholder = "Name, MRN, DOB…" }) {
  const [menu, setMenu] = useState(false);
  const [focused, setFocused] = useState(false);
  const ref = useRef(null), inRef = useRef(null);
  const q = quickSearch(text, forced);
  const open = !collapsible || expanded;
  useEffect(() => { if (collapsible && !open) inRef.current?.blur(); }, [collapsible, open]);
  useEffect(() => {
    if (!menu) return undefined;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setMenu(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [menu]);
  useEffect(() => {
    if (!collapsible) return undefined;
    const h = (e) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const a = document.activeElement, tag = a?.tagName || "";
      if (/input|textarea|select/i.test(tag) || a?.isContentEditable) return;
      e.preventDefault(); onExpandedChange?.(true); setTimeout(() => inRef.current?.focus(), 0);
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [collapsible, onExpandedChange]);
  const clear = () => { setText(""); setForced(null); setMenu(false); inRef.current?.focus(); };
  const chipKey = q?.key || forced;
  const activeCollapsed = collapsible && !open && !!text;
  const reduceMotion = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const showChip = open && !(collapsible && isPhone) && chipKey;

  let sizeStyle;
  if (collapsible) sizeStyle = open ? (isPhone ? { flex: "1 1 auto", minWidth: 0 } : { flex: "0 1 240px", minWidth: 140 }) : { flex: "none", width: 38 };
  else if (width === "100%") sizeStyle = { flex: "1 1 auto", width: "100%", maxWidth: "100%" };
  else if (typeof width === "number") sizeStyle = { flex: "none", width };
  else sizeStyle = { flex: "1 1 200px", maxWidth: 260 };

  const borderCol = focused ? "#5856D6" : "#E9E9EC";
  const expand = () => { if (collapsible && !open) { onExpandedChange?.(true); setTimeout(() => inRef.current?.focus(), 0); } };
  const onBlur = () => {
    setFocused(false);
    if (!collapsible) return;
    setTimeout(() => { if (menu) return; if (!text || isPhone) onExpandedChange?.(false); }, 140);
  };
  return (
    <div ref={ref} onClick={expand} className="er-qs relative inline-flex items-center gap-1.5 min-w-0"
      style={{
        ...sizeStyle, height: inGroup ? 36 : 38, borderRadius: inGroup ? 0 : 10,
        paddingLeft: open ? (inGroup ? 8 : 10) : 0, paddingRight: open ? 4 : 0, justifyContent: open ? undefined : "center",
        border: inGroup ? "none" : (activeCollapsed ? "none" : `1px solid ${borderCol}`),
        boxShadow: focused ? (inGroup ? "inset 0 0 0 2px rgba(88,86,214,.35)" : "0 0 0 3px rgba(88,86,214,.15)") : "none",
        background: inGroup ? "transparent" : (activeCollapsed ? "#F1F1F4" : "#fff"), cursor: open ? "text" : "pointer",
        transition: reduceMotion || !collapsible ? "none" : "flex-basis 160ms, width 160ms",
      }}
      title={activeCollapsed ? `Search: ${text}` : "Search by patient name, MRN, date of birth or visit ID"} data-testid="er-search-box" data-collapsed={!open || undefined}>
      <Search size={15} style={{ color: activeCollapsed ? "#141412" : "#6E6E6B", flex: "none" }} />
      <input ref={inRef} value={text} onChange={(e) => setText(e.target.value)} onFocus={() => setFocused(true)} onBlur={onBlur}
        placeholder={placeholder} aria-label="Search by patient name, MRN, date of birth or visit ID" data-testid="er-search-input"
        tabIndex={open ? 0 : -1} aria-hidden={open ? undefined : true}
        onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); if (menu) setMenu(false); else if (collapsible && !text) onExpandedChange?.(false); else clear(); } }}
        className="min-w-0 text-[13px] outline-none bg-transparent"
        style={{ flex: open ? "1 1 auto" : "none", width: open ? "100%" : 0, opacity: open ? 1 : 0, padding: 0, pointerEvents: open ? "auto" : "none" }} />
      {showChip ? (
        <button type="button" onClick={(e) => { e.stopPropagation(); setMenu((m) => !m); }} aria-haspopup="menu" aria-expanded={menu} data-open={menu || undefined}
          className="er-qs-chip flex-none inline-flex items-center gap-1 h-[26px] px-2 rounded-[7px] text-[12px] font-semibold max-w-[190px]" style={{ background: "#F1F1F4", color: "#141412" }} data-testid="er-search-field">
          <span className="truncate">{QF[chipKey][0]}{q && !q.ok ? <span style={{ color: "#9A5A00" }}> · {QF[chipKey][1]}</span> : null}</span>
          <ChevronDown size={12} className="flex-none" />
        </button>
      ) : null}
      {open && text ? <button type="button" onClick={(e) => { e.stopPropagation(); clear(); }} aria-label="Clear search" className="flex-none w-[24px] h-[24px] grid place-items-center rounded-[7px] text-[#9B9B97] hover:bg-[#F1F1F4] hover:text-[#141412]" data-testid="er-search-close"><X size={14} /></button> : null}
      {open && menu ? (
        <div role="menu" className="absolute right-0 top-[40px] z-40 min-w-[230px] bg-white rounded-[10px] py-1" style={{ border: "1px solid #E9E9EC", boxShadow: "0 12px 32px rgba(20,20,18,.14)" }} data-testid="er-search-menu">
          <div className="px-3 pt-1.5 pb-1 text-[11.5px]" style={{ color: "#9B9B97" }}>Search this text as</div>
          <Item on={!forced} onClick={() => { setForced(null); setMenu(false); }} label="Detect automatically" hint={q ? QF[q.detected][0] : ""} testid="er-search-opt-auto" />
          <div className="my-1 h-px bg-[#F1F1F4]" />
          {Object.entries(QF).map(([k, [l, r]]) => <Item key={k} on={forced === k} onClick={() => { setForced(k); setMenu(false); inRef.current?.focus(); }} label={l} hint={r} testid={`er-search-opt-${k}`} />)}
        </div>
      ) : null}
    </div>
  );
}

const Item = ({ on, onClick, label, hint, testid }) => (
  <button type="button" role="menuitemradio" aria-checked={on} onClick={onClick} className="w-full flex items-center gap-2 px-3 h-[32px] text-[13px] text-left hover:bg-[#F4F4F9]" data-testid={testid}>
    <span className="w-[14px] flex-none">{on ? <Check size={13} /> : null}</span>
    <span className="flex-1" style={{ color: "#141412" }}>{label}</span>
    <span className="text-[11.5px]" style={{ color: "#9B9B97" }}>{hint}</span>
  </button>
);
